import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { TOKEN_KEY, USER_KEY, UNAUTHORIZED_EVENT } from '../api/config';
import { refreshAccessToken } from '../api/client';
import {
    clearAuthStorage,
    getStoredRefreshToken,
    hasStoredSession,
    persistAuthResponse,
} from '../api/session';
import * as api from '../api';
import { setAnalyticsContext, track } from '../utils/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const clearSession = useCallback(() => {
        clearAuthStorage();
        setToken(null);
        setUser(null);
        setAnalyticsContext({ isAuthenticated: false, userLogin: null });
    }, []);

    useEffect(() => {
        let cancelled = false;

        const restoreSession = async () => {
            if (!hasStoredSession()) {
                if (!cancelled) setIsLoading(false);
                return;
            }

            const storedUser = localStorage.getItem(USER_KEY);
            let parsedUser;

            try {
                parsedUser = JSON.parse(storedUser);
            } catch {
                clearSession();
                if (!cancelled) setIsLoading(false);
                return;
            }

            let storedToken = localStorage.getItem(TOKEN_KEY);

            if (!storedToken && getStoredRefreshToken()) {
                try {
                    const data = await refreshAccessToken();
                    storedToken = data.token;
                    if (data.user) {
                        parsedUser = data.user;
                    }
                } catch {
                    clearSession();
                    if (!cancelled) setIsLoading(false);
                    return;
                }
            }

            if (!storedToken) {
                if (!cancelled) setIsLoading(false);
                return;
            }

            if (!cancelled) {
                setToken(storedToken);
                setUser(parsedUser);
            }

            try {
                await api.getProfile();
            } catch {
                // 401 → refresh в interceptor; сеть/5xx — сессию не сбрасываем
            }

            if (!cancelled) setIsLoading(false);
        };

        restoreSession();

        return () => {
            cancelled = true;
        };
    }, [clearSession]);

    useEffect(() => {
        const handleUnauthorized = () => {
            clearSession();
        };

        window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
        return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    }, [clearSession]);

    const login = useCallback(async (loginValue, password) => {
        const data = await api.login(loginValue, password);
        persistAuthResponse(data);
        setToken(data.token);
        setUser(data.user);
        return data;
    }, []);

    const register = useCallback(async (loginValue, password) => {
        const data = await api.register(loginValue, password);
        persistAuthResponse(data);
        setToken(data.token);
        setUser(data.user);
        return data;
    }, []);

    const logout = useCallback(async () => {
        track('auth_logout');
        try {
            await api.logout();
        } catch {
            // ignore logout API errors
        }
        clearSession();
    }, [clearSession]);

    const isAuthenticated = Boolean(token && user);

    useEffect(() => {
        setAnalyticsContext({
            isAuthenticated,
            userLogin: user?.login || null,
        });
    }, [isAuthenticated, user]);

    return (
        <AuthContext.Provider value={{ user, token, isLoading, isAuthenticated, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
